import React, { useState, useCallback } from 'react';
import { Search, TrendingUp, TrendingDown, Download } from 'lucide-react';
import {
    ResponsiveContainer, ComposedChart, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell, Area,
} from 'recharts';

const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];

const TOLERANCIA = 0.01;

const formatarMoeda = (valor) =>
    (parseFloat(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarDiferenca = (valor) => {
    const num = parseFloat(valor) || 0;
    const abs = Math.abs(num).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    if (num > TOLERANCIA) return `+${abs}`;
    if (num < -TOLERANCIA) return `-${abs}`;
    return abs;
};

// Compacto para eixo Y: -1,2k / 3,4k
const formatarCompacto = (valor) => {
    const num = parseFloat(valor) || 0;
    if (Math.abs(num) >= 1000) return `${(num / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
    return num.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

// Verde = equilibrado | Azul = débito (Recebimento > Venda) | Vermelho = crédito (Recebimento < Venda)
const corDiferenca = (d) => {
    if (Math.abs(d) <= TOLERANCIA) return { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0', chart: '#16a34a' };
    if (d > TOLERANCIA) return { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe', chart: '#3b82f6' };
    return { bg: '#fff1f2', text: '#e11d48', border: '#fecdd3', chart: '#f43f5e' };
};

const inputStyle = {
    padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '14px', color: '#334155', backgroundColor: '#fff',
    outline: 'none', width: '100%', boxSizing: 'border-box',
};
const labelStyle = {
    display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px',
};
const cardBox = { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' };

const TooltipMoeda = ({ active, payload, label, sufixoLabel }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <p style={{ margin: '0 0 6px 0', fontWeight: '800', color: '#0f172a' }}>{sufixoLabel ? `${sufixoLabel} ${label}` : label}</p>
            {payload.map((p) => (
                <p key={p.dataKey} style={{ margin: '2px 0', color: p.color, fontWeight: '600' }}>
                    {p.name}: {formatarMoeda(p.value)}
                </p>
            ))}
        </div>
    );
};

const FinanceiroFitaSobraFalta = () => {
    const agora = new Date();
    const mesDefault = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;

    const [mes, setMes] = useState(mesDefault);
    const [loja, setLoja] = useState('');               // '' = Todas
    const [resultado, setResultado] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    const token = localStorage.getItem('authToken');

    const consultar = useCallback(async () => {
        if (!mes) { setErro('Selecione o mês.'); return; }
        setCarregando(true); setErro(''); setResultado(null);
        try {
            const qs = `mes=${mes}${loja ? `&loja=${encodeURIComponent(loja)}` : ''}`;
            const res = await fetch(`/api/fita/sobra-falta?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || `Erro ${res.status}`); }
            setResultado(await res.json());
        } catch (e) {
            setErro(e.message || 'Erro ao consultar dados.');
        } finally {
            setCarregando(false);
        }
    }, [mes, loja, token]);

    const exportarCSV = useCallback(() => {
        if (!resultado) return;
        const porLoja = !resultado.loja;
        const cabecalho = porLoja
            ? ['Loja', 'Recebimento (R$)', 'Venda Sovos (R$)', 'Diferenca (R$)']
            : ['Data', 'Recebimento (R$)', 'Venda Sovos (R$)', 'Diferenca (R$)', 'Acumulado (R$)'];
        const corpo = porLoja
            ? resultado.lojas.map(l => [l.loja, l.recebimento, l.venda, l.diferenca].map((v, i) => i === 0 ? v : (v || 0).toFixed(2).replace('.', ',')))
            : resultado.dias.map(d => [d.dia.split('-').reverse().join('/'), d.recebimento, d.venda, d.diferenca, d.acumulado].map((v, i) => i === 0 ? v : (v || 0).toFixed(2).replace('.', ',')));
        const linhas = [cabecalho, ...corpo];
        const csv = '﻿' + linhas.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `debito_credito_${resultado.loja || 'todas'}_${resultado.mes}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [resultado]);

    const porLoja = resultado && !resultado.loja;

    // Dados dos gráficos
    const dadosLinha = resultado ? resultado.dias.map(d => ({
        dia: d.dia.slice(8, 10),
        diaCompleto: d.dia.split('-').reverse().join('/'),
        acumulado: d.acumulado,
        diferenca: d.diferenca,
    })) : [];

    const dadosBarra = resultado
        ? (porLoja
            ? resultado.lojas.map(l => ({ rotulo: l.loja, diferenca: l.diferenca }))
            : resultado.dias.map(d => ({ rotulo: d.dia.slice(8, 10), diferenca: d.diferenca })))
        : [];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>
                    Débito / Crédito — Recebimento × Venda
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>
                    Acompanhe ao longo do mês se o débito ou crédito entre o Recebimento (Atos) e a Venda (Sovos) tende a se compensar.
                </p>
            </div>

            {/* FILTROS */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '16px', alignItems: 'flex-end' }}>
                    <div>
                        <label style={labelStyle}>Mês</label>
                        <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Loja</label>
                        <select value={loja} onChange={e => setLoja(e.target.value)} style={inputStyle}>
                            <option value="">Todas as lojas</option>
                            {LOJAS.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={consultar}
                        disabled={carregando || !mes}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '10px 24px', borderRadius: '8px', border: 'none', height: '42px',
                            whiteSpace: 'nowrap', fontWeight: '800', fontSize: '14px',
                            cursor: (!mes || carregando) ? 'not-allowed' : 'pointer',
                            backgroundColor: (!mes || carregando) ? '#e2e8f0' : '#16a39c',
                            color: (!mes || carregando) ? '#94a3b8' : '#fff',
                        }}
                    >
                        <Search size={16} />
                        {carregando ? 'Consultando...' : 'Consultar'}
                    </button>
                </div>
            </div>

            {erro && (
                <div style={{ padding: '14px 18px', borderRadius: '10px', backgroundColor: '#fff1f2', border: '1px solid #fecdd3', color: '#e11d48', fontWeight: '600', fontSize: '14px' }}>
                    {erro}
                </div>
            )}

            {resultado && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* CABEÇALHO + EXPORTAR */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                        <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                            {resultado.loja ? <>Loja <strong>{resultado.loja}</strong></> : <strong>Todas as lojas</strong>} · {resultado.mes.split('-').reverse().join('/')}
                            {resultado.dataLimite && <> · dados completos até <strong>{resultado.dataLimite.split('-').reverse().join('/')}</strong></>}
                        </span>
                        <button
                            onClick={exportarCSV}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 18px', borderRadius: '8px', border: '1px solid #16a39c', backgroundColor: '#f0fdfa', color: '#16a39c', fontWeight: '700', fontSize: '13px', cursor: 'pointer' }}
                        >
                            <Download size={15} /> Exportar CSV
                        </button>
                    </div>

                    {/* AVISO: sem dados da Atos no mês */}
                    {!resultado.dataLimite && (
                        <div style={{ padding: '14px 18px', borderRadius: '10px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontWeight: '600', fontSize: '14px' }}>
                            Nenhum dia completo da Atos neste mês — é preciso ter todas as bandeiras principais (APP, Dinheiro, iFood, Pix, TEF Crédito e TEF Débito) com valor. Importe os arquivos (cartões e dinheiro) na aba "Importação".
                        </div>
                    )}

                    {/* KPIs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                        {(() => {
                            const cor = corDiferenca(resultado.totais.diferenca);
                            const cards = [
                                { label: 'Recebimento Atos', valor: formatarMoeda(resultado.totais.recebimento), bg: '#eff6ff', cor: '#3b82f6', icon: <TrendingUp size={22} /> },
                                { label: 'Venda Sovos', valor: formatarMoeda(resultado.totais.venda), bg: '#f0fdfa', cor: '#16a39c', icon: <TrendingUp size={22} /> },
                                {
                                    label: resultado.totais.diferenca >= 0 ? 'Débito Acumulado (Mês)' : 'Crédito Acumulado (Mês)',
                                    valor: formatarDiferenca(resultado.totais.diferenca),
                                    bg: cor.bg, cor: cor.text,
                                    icon: resultado.totais.diferenca >= 0 ? <TrendingUp size={22} /> : <TrendingDown size={22} />,
                                },
                            ];
                            return cards.map(c => (
                                <div key={c.label} style={{ ...cardBox, display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: c.bg, color: c.cor }}>{c.icon}</div>
                                    <div>
                                        <p style={{ margin: 0, fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>{c.label}</p>
                                        <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{c.valor}</p>
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>

                    {/* GRÁFICO: ACUMULADO NO MÊS */}
                    <div style={cardBox}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>Diferença acumulada no mês</h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#64748b' }}>
                            Se a linha tende a voltar para o zero, o débito e o crédito estão se compensando.
                        </p>
                        {dadosLinha.length === 0 ? (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>Sem dados no período.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <ComposedChart data={dadosLinha} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="gradAcum" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#16a39c" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#16a39c" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                    <XAxis dataKey="dia" tick={{ fontSize: 12, fill: '#64748b' }} />
                                    <YAxis tickFormatter={formatarCompacto} tick={{ fontSize: 12, fill: '#64748b' }} width={55} />
                                    <Tooltip content={<TooltipMoeda sufixoLabel="Dia" />} />
                                    <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} />
                                    <Area type="monotone" dataKey="acumulado" name="Acumulado" stroke="#16a39c" strokeWidth={2.5} fill="url(#gradAcum)" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* GRÁFICO: BARRAS (por dia ou por loja) */}
                    <div style={cardBox}>
                        <h4 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                            {porLoja ? 'Débito / Crédito por loja (mês)' : 'Débito / Crédito por dia'}
                        </h4>
                        {dadosBarra.length === 0 ? (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontWeight: '600' }}>Sem dados no período.</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={dadosBarra} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                    <XAxis dataKey="rotulo" tick={{ fontSize: 12, fill: '#64748b' }} />
                                    <YAxis tickFormatter={formatarCompacto} tick={{ fontSize: 12, fill: '#64748b' }} width={55} />
                                    <Tooltip content={<TooltipMoeda sufixoLabel={porLoja ? '' : 'Dia'} />} />
                                    <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} />
                                    <Bar dataKey="diferenca" name="Diferença" radius={[3, 3, 0, 0]}>
                                        {dadosBarra.map((d, i) => <Cell key={i} fill={corDiferenca(d.diferenca).chart} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* TABELA */}
                    <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                        {(porLoja
                                            ? ['Loja', 'Recebimento', 'Venda Sovos', 'Diferença']
                                            : ['Data', 'Recebimento', 'Venda Sovos', 'Diferença', 'Acumulado']
                                        ).map((h, i) => (
                                            <th key={h} style={{ padding: '12px 20px', textAlign: i === 0 ? 'left' : 'right', fontWeight: '800', color: '#475569', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(porLoja ? resultado.lojas : resultado.dias).map((row, idx) => {
                                        const cores = corDiferenca(row.diferenca);
                                        const primeira = porLoja ? row.loja : row.dia.split('-').reverse().join('/');
                                        return (
                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                                <td style={{ padding: '12px 20px', color: '#0f172a', fontWeight: '600' }}>{primeira}</td>
                                                <td style={{ padding: '12px 20px', textAlign: 'right', color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{formatarMoeda(row.recebimento)}</td>
                                                <td style={{ padding: '12px 20px', textAlign: 'right', color: '#334155', fontVariantNumeric: 'tabular-nums' }}>{formatarMoeda(row.venda)}</td>
                                                <td style={{ padding: '12px 20px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', backgroundColor: cores.bg, color: cores.text, border: `1px solid ${cores.border}` }}>
                                                        {formatarDiferenca(row.diferenca)}
                                                    </span>
                                                </td>
                                                {!porLoja && (
                                                    <td style={{ padding: '12px 20px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: '700', color: corDiferenca(row.acumulado).text }}>
                                                        {formatarDiferenca(row.acumulado)}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr style={{ backgroundColor: '#f0fdfa', borderTop: '2px solid #0d7a74' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: '800', color: '#0d7a74' }}>TOTAL</td>
                                        <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0d7a74', fontVariantNumeric: 'tabular-nums' }}>{formatarMoeda(resultado.totais.recebimento)}</td>
                                        <td style={{ padding: '14px 20px', textAlign: 'right', fontWeight: '800', color: '#0d7a74', fontVariantNumeric: 'tabular-nums' }}>{formatarMoeda(resultado.totais.venda)}</td>
                                        <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                                            {(() => {
                                                const cores = corDiferenca(resultado.totais.diferenca);
                                                return (
                                                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '800', backgroundColor: cores.bg, color: cores.text, border: `1px solid ${cores.border}` }}>
                                                        {formatarDiferenca(resultado.totais.diferenca)}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        {!porLoja && <td style={{ padding: '14px 20px' }} />}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        {/* LEGENDA */}
                        <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#bfdbfe' }}></span> Débito (Recebimento {'>'} Venda)
                            </span>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#fecdd3' }}></span> Crédito (Recebimento {'<'} Venda)
                            </span>
                            <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#bbf7d0' }}></span> Equilibrado
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceiroFitaSobraFalta;
